import PossiblePennyBadge from "./PossiblePennyBadge";
import StoreSearchSortControls from "./StoreSearchSortControls";
import {
  getNearbyDiscountPercentage,
  getNearbyMsrp,
  isBestNearbyDeal,
  isNearbyStoreInStock,
  isTargetLimitedStock,
} from "./NearbyResults.utils";
import {
  getStoreSearchDisplayPrice,
  sortStoreSearchResults,
  type StoreSearchSort,
} from "../utils/storeSearchSort";
import { useTranslation } from "../context/LanguageContext";
import { samsClubStockLabel } from "../utils/stockCount";

// Sam's Club reports clubs it could not check as status UNKNOWN (with the
// per-club error). Show those as unavailable instead of hiding them or calling
// them out of stock.
const isUnknownStockResult = (retailer: string, result: { status?: string }) =>
  retailer === "samsclub" && result.status?.toUpperCase() === "UNKNOWN";

interface NearbyStoreResultsListProps {
  results: NearbyStoreResult[];
  retailer: string;
  storeSort: StoreSearchSort;
  onSortChange: (sort: StoreSearchSort) => void;
  productMsrp?: number;
  scrollable?: boolean;
}

interface NearbyStoreResult {
  storeId: string;
  storeAddress: string;
  storePrice: number;
  msrp?: number;
  originalPrice?: number;
  price?: number;
  inventory?: number;
  backroom?: number;
  status?: string;
  aisles?: string;
  distance?: string;
  possiblePenny?: boolean;
  verificationStatus?: string;
  error?: string;
}

const formatPrice = (price: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price);

/**
 * Canonical nearby-store result presentation used by both the Nearby sheet and
 * the dedicated product-search page. Keeping the markup here prevents the two
 * search entry points from drifting visually or semantically.
 */
export default function NearbyStoreResultsList({
  results,
  retailer,
  storeSort,
  onSortChange,
  productMsrp,
  scrollable = true,
}: NearbyStoreResultsListProps) {
  const { t } = useTranslation();
  const sorted = sortStoreSearchResults(
    results.filter((result) => !result.error || isUnknownStockResult(retailer, result)),
    storeSort,
    retailer,
  );
  const displayedResults = storeSort === "best_deal"
    ? [
        ...sorted.filter(isNearbyStoreInStock),
        ...sorted.filter((result) => !isNearbyStoreInStock(result)),
      ]
    : sorted;
  const inStockCount = displayedResults.filter(isNearbyStoreInStock).length;
  const pricedInStockResults = displayedResults.filter(
    (result) =>
      isNearbyStoreInStock(result) &&
      Number.isFinite(getStoreSearchDisplayPrice(result)) &&
      getStoreSearchDisplayPrice(result) > 0,
  );
  const cheapestPrice = pricedInStockResults.length
    ? Math.min(...pricedInStockResults.map(getStoreSearchDisplayPrice))
    : Infinity;
  const hasTargetClearance = displayedResults.some(
    (result) =>
      isTargetLimitedStock(retailer, result.status) &&
      getNearbyDiscountPercentage(result.storePrice, getNearbyMsrp(result, productMsrp)) !== null,
  );

  return (
    <div className={scrollable ? "flex min-h-0 flex-1 flex-col" : undefined}>
      <div className="flex-shrink-0 border-t border-gray-100 px-4 pb-2.5 pt-3 dark:border-dark-700 sm:px-5">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1" role="status">
          <p className="text-xs font-semibold text-gray-800 dark:text-dark-100">
            {inStockCount > 0
              ? t("products.nearbyInStockCount", { count: inStockCount })
              : t("products.nearbyNoStock")}
          </p>
          <span className="text-[10px] text-gray-500 dark:text-dark-300">
            {t("products.nearbyStoresChecked", { count: displayedResults.length })}
          </span>
        </div>
        <StoreSearchSortControls
          value={storeSort}
          onChange={onSortChange}
          bestDealLabel={t("products.storeSortLowestPrice")}
        />
      </div>

      <div
        className={`${scrollable ? "min-h-0 flex-1 overflow-y-auto" : ""} space-y-1.5 px-3 pb-6 sm:px-4`}
      >
        {displayedResults.map((result, index) => {
          const isInStock = isNearbyStoreInStock(result);
          const isUnknownStock = isUnknownStockResult(retailer, result);
          const isPossiblePenny = retailer === "home_depot" && result.possiblePenny;
          const isClearance = !isPossiblePenny && isTargetLimitedStock(retailer, result.status);
          const msrp = getNearbyMsrp(result, productMsrp);
          const discountPercentage = getNearbyDiscountPercentage(result.storePrice, msrp);
          const isBest = isInStock && isBestNearbyDeal({
            retailer,
            status: result.status,
            storePrice: getStoreSearchDisplayPrice(result),
            msrp,
            cheapestPrice,
            hasTargetClearance,
          });
          const isPenny =
            isPossiblePenny || result.storeId?.includes("-PENNY-ITEM") || result.storePrice === 0.01;
          const storeLabel = retailer === "costco"
            ? t("card.warehouseNumber", { id: result.storeId?.replace(/-wh$/, "") ?? "" })
            : t("products.storeNumber", {
                id: result.storeId?.replace("-PENNY-ITEM", "") ?? "",
              });

          return (
            <div
              key={`${result.storeId}-${index}`}
              className={`rounded-xl border px-3 py-2.5 ${
                isBest
                  ? "border-emerald-300 bg-emerald-50 dark:border-emerald-600 dark:bg-emerald-950/30"
                  : !isInStock
                    ? "border-stone-200 bg-stone-100 dark:border-dark-700 dark:bg-dark-900"
                    : "border-gray-300 bg-white dark:border-dark-500 dark:bg-dark-800"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1 pr-3">
                  <div className="mb-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <h5 className="text-[13px] font-semibold text-gray-900 dark:text-white">{storeLabel}</h5>
                    {isBest && (
                      <span className="rounded bg-emerald-500 px-1.5 py-0.5 text-[9px] font-extrabold text-white">
                        {t("card.bestDeal")}
                      </span>
                    )}
                  </div>
                  <p className="mb-1 truncate text-[11px] text-gray-500 dark:text-dark-200">
                    {result.storeAddress}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {isPossiblePenny && <PossiblePennyBadge />}
                    {retailer === "home_depot" &&
                      (result.verificationStatus === "pending" || result.verificationStatus === "unverified") && (
                        <span className="text-[10px] text-gray-500 dark:text-dark-200">
                          {t(
                            result.verificationStatus === "pending"
                              ? "products.pickupChecking"
                              : "products.pickupUnverified",
                          )}
                        </span>
                      )}
                    {isUnknownStock ? (
                      <span
                        className="inline-flex max-w-full items-center gap-1 truncate rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-dark-700 dark:text-dark-200"
                        title={result.error || undefined}
                      >
                        {result.error || t("card.unavailable")}
                      </span>
                    ) : !isInStock ? (
                      <span className="inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-950/40 dark:text-red-300">
                        <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-red-600 dark:bg-red-400" />
                        {t("products.outOfStockLabel")}
                      </span>
                    ) : retailer === "costco" ? (
                      <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        {t("products.inStock")}
                      </span>
                    ) : retailer === "samsclub" ? (
                      <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        {samsClubStockLabel(retailer, result.inventory ?? 0, {
                          inStock: t("products.inStock"),
                          lowStock: t("products.lowStock"),
                        }) ?? t("products.inStock")}
                      </span>
                    ) : retailer === "tractor_supply" ? (
                      <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        {typeof result.inventory === "number"
                          ? t("products.inStockCount", { count: String(result.inventory ?? 0) })
                          : t("products.inStock")}
                      </span>
                    ) : (
                      <>
                        <span className="inline-flex items-center gap-1 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                          {result.inventory ?? 0}{" "}
                          {retailer === "walmart"
                            ? t("products.onHandLabel")
                            : t("products.salesFloorLabel")}
                        </span>
                        {retailer !== "walmart" && (result.backroom ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                            {result.backroom ?? 0} {t("products.backroomLabel")}
                          </span>
                        )}
                      </>
                    )}
                    {isInStock && (
                      <span className="text-[10px] text-gray-500 dark:text-dark-200">
                        {result.aisles
                          ? `${t("products.aisle")} ${result.aisles}`
                          : t("products.noLocation")}
                      </span>
                    )}
                    {result.distance && (
                      <span className="text-[10px] text-gray-500 dark:text-dark-200">{result.distance}</span>
                    )}
                  </div>
                </div>

                <div className="flex flex-shrink-0 flex-col items-end">
                  <div className="flex flex-col items-end gap-0.5">
                    {isClearance ? (
                      <span className="text-sm font-bold text-purple-700 dark:text-purple-300">
                        {t("card.onClearance")}
                      </span>
                    ) : isPenny ? (
                      <span className="text-lg font-bold text-red-600">$0.01</span>
                    ) : (
                      <span
                        className={`text-base font-bold ${
                          isBest
                            ? "text-emerald-600 dark:text-emerald-400"
                            : !isInStock
                              ? "text-[15px] font-semibold text-gray-400 dark:text-dark-400"
                              : "text-gray-900 dark:text-white"
                        }`}
                      >
                        {Number.isFinite(result.storePrice) && result.storePrice > 0
                          ? formatPrice(result.storePrice)
                          : "—"}
                      </span>
                    )}
                    {!isClearance && !isPossiblePenny && discountPercentage !== null && (
                      <span
                        className={`text-[10px] font-semibold ${
                          !isInStock
                            ? "text-gray-400 dark:text-dark-400"
                            : "text-emerald-700 dark:text-emerald-300"
                        }`}
                      >
                        {t("card.percentOffUpper", {
                          percent: discountPercentage === 0 ? "<1" : discountPercentage,
                        })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
